import { ComponentType } from '@angular/cdk/portal';
import { Injectable } from '@angular/core';
import { MatSnackBar, MatSnackBarConfig, MatSnackBarRef } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';

import { SnackbarAlertComponent } from '../../components/snackbar-alert/snackbar-alert.component';

const DEFAULT_PANEL_CLASS = ['snackbar', 'widget'];
const ACTION_PANEL_CLASS = [...DEFAULT_PANEL_CLASS, 'snackbar-with-action'];
const WARNING_PANEL_CLASS = [...DEFAULT_PANEL_CLASS, 'snackbar-warning'];

@Injectable({ providedIn: 'root' })
export class SnackbarService {
  constructor(private readonly snackBar: MatSnackBar, private readonly translate: TranslateService) {}

  showWithBoldLead(
    titleKey: string,
    messageKey: string,
    options?: {
      panelClass?: string[];
      duration?: number;
      dismissible?: boolean;
      icon?: 'ri-save-line' | 'ri-close-fill' | 'ri-error-warning-fill' | 'ri-time-line';
    }
  ): MatSnackBarRef<SnackbarAlertComponent> {
    const title = this.translate.instant(titleKey);
    const message = this.translate.instant(messageKey);
    const action = options?.dismissible === false ? undefined : 'core.dismiss';
    const duration = options?.duration ?? this.calculateDuration(`${title} ${message}`);

    return this.snackBar.openFromComponent(SnackbarAlertComponent, {
      duration,
      panelClass: options?.panelClass ?? WARNING_PANEL_CLASS,
      data: {
        icon: options?.icon ?? 'ri-error-warning-fill',
        title: titleKey,
        message: messageKey,
        action,
      },
    });
  }

  /**
   * Show a translated snackbar. Duration auto-calculates from the message length unless
   * an explicit `duration` is passed (use `0` to keep the snackbar open until dismissed by the caller).
   * Set `dismissible: false` to suppress the dismiss action (useful for progress snackbars the user
   * cannot opt out of).
   */
  show(
    translationKey: string,
    options?: {
      panelClass?: string[];
      duration?: number;
      dismissible?: boolean;
      params?: Record<string, unknown>;
    }
  ): MatSnackBarRef<unknown> {
    const message = this.translate.instant(translationKey, options?.params);
    const action = options?.dismissible === false ? undefined : this.translate.instant('core.dismiss');
    const duration = options?.duration ?? this.calculateDuration(message);

    return this.snackBar.open(message, action, {
      duration,
      panelClass: options?.panelClass ?? DEFAULT_PANEL_CLASS,
    });
  }

  showWithAction(
    messageKey: string,
    actionKey: string,
    onAction: () => void,
    options?: { panelClass?: string[]; duration?: number; dismissible?: boolean }
  ): MatSnackBarRef<SnackbarAlertComponent> {
    return this.snackBar.openFromComponent(SnackbarAlertComponent, {
      duration: options?.duration ?? 0,
      panelClass: options?.panelClass ?? ACTION_PANEL_CLASS,
      data: {
        title: messageKey,
        action: actionKey,
        onAction,
        dismissible: options?.dismissible,
      },
    });
  }

  /**
   * Open a custom snackbar component, applying default panelClass if not provided.
   */
  openFromComponent<T>(component: ComponentType<T>, config?: MatSnackBarConfig): MatSnackBarRef<T> {
    return this.snackBar.openFromComponent(component, {
      ...config,
      panelClass: config?.panelClass ?? DEFAULT_PANEL_CLASS,
    });
  }

  private calculateDuration(message: string): number {
    const baseDuration = 3000;
    const estimatedLines = Math.ceil(message.length / 50);
    const additionalTime = Math.max(0, (estimatedLines - 1) * 1500);
    const totalDuration = Math.min(baseDuration + additionalTime, 10000);
    return totalDuration >= 10000 ? 0 : totalDuration;
  }
}
